import dayjs from 'dayjs';
import { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import {
    BarChart3,
    BookOpen,
    CalendarDays,
    CheckCircle2,
    Clock3,
    Filter,
    Info,
    Plus,
    RefreshCw,
    Search,
    Target,
    Trash2,
    UserRound,
    UsersRound,
    X,
} from 'lucide-react';
import { getCoachNameListAPI } from '../../api/center';
import {
    createNewOpportunityAPI,
    createNewOpportunityCategoryAPI,
    deleteNewOpportunityCategoryAPI,
    getNewOpportunitiesAPI,
    getNewOpportunityCategoriesAPI,
    getNewOpportunityMemberMatchAPI,
    getNewOpportunityJournalAPI,
    getNewOpportunitySummaryAPI,
    NewOpportunityDTO,
    updateNewOpportunityAPI,
    updateNewOpportunityCategoryAPI,
} from '../../api/newOpportunity';
import { getItemWithExpiration } from '../../lib/utils';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import {
    calculateCursorAfterFormat,
    formatNewOpportunityPhoneNumber,
    normalizeNewOpportunityPhoneDigits,
} from '../../utils/newOpportunityPhone';
import NewOpportunityJournalReadOnly from '../../components/NewOpportunity/NewOpportunityJournalReadOnly';
import { isMemberRecordReadOnly } from '../../features/publicMember/types';

type CoachOption = {
    coachId: string;
    name: string;
};

type Category = {
    _id?: string;
    id?: string;
    name: string;
    isActive?: boolean;
    isDefault?: boolean;
};

type OpportunityRow = NewOpportunityDTO & {
    activeMemberAtCreate?: boolean;
    converted?: boolean;
    convertedAt?: string;
    paymentDate?: string;
    conversionSourceType?: string;
};

type SummaryStats = {
    opportunityCount?: number;
    leadMemberCount?: number;
    closedMemberCount?: number;
    closingRate?: number;
    avgOpportunitiesPerMember?: number;
    avgOpportunityOrderToClose?: number;
    coachId?: string;
    coachName?: string;
    categoryId?: string;
    categoryName?: string;
    completedOpportunityCount?: number;
    journalWriteTarget?: number;
    journalWriteWritten?: number;
    journalWriteRate?: number;
    appLinkedCount?: number;
    appMemberCount?: number;
    appLinkedRate?: number;
    appLinkedAtCreateCount?: number;
    appLinkTargetCount?: number;
    appLinkedConversionCount?: number;
    appLinkedConversionRate?: number;
    rateUnit?: 'PERCENT';
};

type SummaryConversionRow = {
    centerId?: string;
    centerName?: string;
    coachId?: string;
    coachName?: string;
    memberId?: string;
    memberName?: string;
    memberPhoneNumber?: string;
    firstOpportunityAt?: string;
    categoryName?: string;
    latestCategoryName?: string;
    opportunityCategoryNames?: string[];
    converted?: boolean;
    convertedAt?: string;
    paymentDate?: string;
    opportunityCount?: number;
    opportunityOrderToClose?: number;
    conversionSourceType?: string;
    conversionSourceName?: string;
};

const toDateTimeLocal = (date: dayjs.Dayjs) => date.format('YYYY-MM-DDTHH:mm');
const fromDateTimeLocal = (value: string) => dayjs(value).toISOString();
const getId = (item: any) => item?._id || item?.id || item?.newOpportunityId || '';
export const isNewOpportunityRecordReadOnly = (item?: Partial<NewOpportunityDTO> | null) =>
    isMemberRecordReadOnly(item);

// 오전/오후 + 시 + 분 3개 dropdown 헬퍼
const HOUR_OPTIONS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTE_OPTIONS_10 = [0, 10, 20, 30, 40, 50];

const splitClock = (
    value: string
): { period: 'AM' | 'PM'; hour12: number; minute: number } => {
    if (!value) return { period: 'AM', hour12: 9, minute: 0 };
    const [hStr, mStr] = value.split(':');
    const h24 = Number(hStr);
    const mRaw = Number(mStr);
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return {
        period: h24 < 12 ? 'AM' : 'PM',
        hour12: Number.isFinite(h12) ? h12 : 9,
        minute: Number.isFinite(mRaw) ? Math.floor(mRaw / 10) * 10 : 0,
    };
};
const joinClock = (parts: { period: 'AM' | 'PM'; hour12: number; minute: number }): string => {
    let h24 = parts.hour12 % 12;
    if (parts.period === 'PM') h24 += 12;
    return `${String(h24).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
};

// "YYYY-MM-DDTHH:mm" 을 date / time 으로 분리하거나 결합하는 헬퍼
const splitDateTimeLocal = (value: string): { date: string; time: string } => {
    if (!value) return { date: '', time: '' };
    const [datePart = '', timePart = ''] = value.split('T');
    return { date: datePart, time: timePart };
};
const joinDateTimeLocal = (date: string, time: string): string => {
    if (!date) return '';
    return `${date}T${time || '00:00'}`;
};
const numberFormat = new Intl.NumberFormat('ko-KR');
const formatCount = (value?: number) => numberFormat.format(value || 0);
const normalizeRatePercent = (value?: number | null, rateUnit?: string) => {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
        return null;
    }
    const numeric = Number(value);
    return rateUnit === 'PERCENT' ? numeric : Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
};
const formatRate = (value?: number | null, rateUnit?: string) => {
    const percent = normalizeRatePercent(value, rateUnit);
    return percent === null ? '-' : `${Math.round(percent * 10) / 10}%`;
};
const formatTargetRate = (value?: number | null, target?: number, rateUnit?: string) =>
    Number(target || 0) > 0 ? formatRate(value, rateUnit) : '-';
const formatAverage = (value?: number) => `${Number(value || 0).toFixed(Number.isInteger(value || 0) ? 0 : 1)}건`;
const limitMemberName = (value: string) => value.slice(0, 5);
const formatConversionSource = (sourceType?: string) => (sourceType === 'memberTicket' ? '그룹수강권' : '수강권');
const formatDateTime = (value?: string, format = 'YYYY.MM.DD HH:mm') => (value && dayjs(value).isValid() ? dayjs(value).format(format) : '-');
const getJournalEmptyLabel = (row: OpportunityRow) => {
    if (row.journalEligible === true) return '대상 미작성';
    if (row.journalExcludedReason === 'NOT_STARTED') {
        return row.canWriteWorkoutJournal ? '작성 가능 · 집계 전' : '작성 가능 예정';
    }
    if (
        row.journalExcludedReason === 'STATUS_NO_SHOW' ||
        row.journalExcludedReason === 'POLICY_NOT_ENROLLED' ||
        row.journalExcludedReason === 'INACTIVE'
    ) {
        return '작성률 대상 아님';
    }
    return row.hasWorkoutJournal === false ? '미작성' : '확인 필요';
};
const OPPORTUNITY_PAGE_SIZE = 20;
const CLOSING_PAGE_SIZE = 8;
type QuickRange = 'month' | 'lastMonth' | 'week';

const getInitialSearchParam = (key: string, fallback = '') => {
    if (typeof window === 'undefined') return fallback;
    return new URLSearchParams(window.location.search).get(key) || fallback;
};

const getInitialJournalOpportunity = (): OpportunityRow | null => {
    const newOpportunityId = getInitialSearchParam('journalId');
    if (!newOpportunityId) return null;
    const startTime = getInitialSearchParam('journalStartTime');
    return {
        newOpportunityId,
        displayName: getInitialSearchParam('journalTitle') || undefined,
        startTime,
        endTime: startTime,
        hasWorkoutJournal: true,
    };
};

const getInitialDateFilter = (key: string, fallback: string) => {
    const candidate = getInitialSearchParam(key);
    return /^\d{4}-\d{2}-\d{2}$/.test(candidate) && dayjs(candidate).isValid()
        ? candidate
        : fallback;
};

type NewOpportunityPageProps = {
    createAlert?: any;
    embedded?: boolean;
};

const NewOpportunityPage = ({ createAlert, embedded = false }: NewOpportunityPageProps) => {
    const authObject = getItemWithExpiration('authObject');
    const authorization = getItemWithExpiration('authorization');
    const centerId = authObject?.centerId || '';
    const queryClient = useQueryClient();
    const { isNewOpportunityEnabled, isLoading: isFeatureFlagsLoading } = useCenterFeatureFlags();
    const closingListRef = useRef<HTMLElement | null>(null);
    const opportunityListRef = useRef<HTMLElement | null>(null);

    const phoneInputRef = useRef<HTMLInputElement>(null);

    const [startDate, setStartDate] = useState(() =>
        getInitialDateFilter('startDate', dayjs().startOf('month').format('YYYY-MM-DD'))
    );
    const [endDate, setEndDate] = useState(() =>
        getInitialDateFilter(
            'endDate',
            dayjs().endOf('month').format('YYYY-MM-DD')
        )
    );
    const [coachId, setCoachId] = useState(() => getInitialSearchParam('coachId'));
    const [categoryId, setCategoryId] = useState(() =>
        getInitialSearchParam('categoryId')
    );
    const [keyword, setKeyword] = useState('');
    const [keywordInput, setKeywordInput] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshedAt, setRefreshedAt] = useState(dayjs());
    const [page, setPage] = useState(1);
    const [memoDrafts, setMemoDrafts] = useState<Record<string, string>>({});
    const [updatingMemoId, setUpdatingMemoId] = useState('');
    const [journalOpportunity, setJournalOpportunity] = useState<OpportunityRow | null>(
        getInitialJournalOpportunity
    );
    const [journalEligibleFilter, setJournalEligibleFilter] = useState(
        () =>
            getInitialSearchParam('journalEligible') === 'true' ||
            getInitialSearchParam('journalEvidence') === 'true'
    );
    const [evidenceAsOf, setEvidenceAsOf] = useState<string | undefined>(
        () => getInitialSearchParam('asOf') || undefined
    );
    const isInvalidDateRange =
        !dayjs(startDate).isValid() ||
        !dayjs(endDate).isValid() ||
        dayjs(startDate).isAfter(dayjs(endDate));
    const [form, setForm] = useState({
        coachId: '',
        memberName: '',
        phoneNumber: '',
        categoryId: '',
        startTime: toDateTimeLocal(dayjs().add(1, 'hour').startOf('hour')),
        endTime: toDateTimeLocal(dayjs().add(1, 'hour').startOf('hour').add(50, 'minute')),
        memo: '',
        isSendAlimtalkToMember: true,
    });

    const notify = (message: string) => {
        if (createAlert) createAlert('', message);
        else alert(message);
    };

    const categoryQuery = useQuery({
        queryKey: ['newOpportunityCategories', centerId],
        queryFn: () => getNewOpportunityCategoriesAPI(centerId, authorization),
        enabled: isNewOpportunityEnabled && !!centerId && !!authorization,
    });
    const categories: Category[] = categoryQuery.data?.data?.results || [];
    const activeCategories = categories.filter((category) => category.isActive !== false);

    const coachQuery = useQuery({
        queryKey: ['newOpportunityCoaches', centerId],
        queryFn: () => getCoachNameListAPI(centerId, authorization),
        enabled: isNewOpportunityEnabled && !!centerId && !!authorization,
    });
    const coaches: CoachOption[] = coachQuery.data?.data?.coachNameList || [];

    const selectedCategory = categories.find((category) => getId(category) === form.categoryId);
    const phoneDigits = normalizeNewOpportunityPhoneDigits(form.phoneNumber);

    const listParams = useMemo(
        () => ({
            startTime: dayjs(startDate).startOf('day').toISOString(),
            endTime: dayjs(endDate).add(1, 'day').startOf('day').toISOString(),
            coachId,
            categoryId,
            journalEligible: journalEligibleFilter ? ('true' as const) : undefined,
            asOf: journalEligibleFilter ? evidenceAsOf : undefined,
            includeActiveMembers: 'true',
            keyword: keyword.trim(),
            page: String(page),
            limit: String(OPPORTUNITY_PAGE_SIZE),
        }),
        [
            startDate,
            endDate,
            coachId,
            categoryId,
            journalEligibleFilter,
            evidenceAsOf,
            keyword,
            page,
        ]
    );

    const summaryParams = useMemo(
        () => ({
            startTime: dayjs(startDate).startOf('day').toISOString(),
            endTime: dayjs(endDate).add(1, 'day').startOf('day').toISOString(),
            coachId,
            categoryId,
            includeActiveMembers: 'true',
        }),
        [startDate, endDate, coachId, categoryId]
    );

    const opportunityQuery = useQuery({
        queryKey: ['newOpportunities', centerId, listParams],
        queryFn: () => getNewOpportunitiesAPI(centerId, authorization, listParams),
        enabled:
            isNewOpportunityEnabled &&
            !!centerId &&
            !!authorization &&
            !isInvalidDateRange,
    });
    const opportunities: OpportunityRow[] = opportunityQuery.data?.data?.results || [];
    const totalResults = opportunityQuery.data?.data?.totalResults ?? opportunities.length;
    const pageCount = Math.max(Math.ceil(totalResults / OPPORTUNITY_PAGE_SIZE), 1);
    const displayPage = Math.min(page, pageCount);
    const pageStart = totalResults ? (displayPage - 1) * OPPORTUNITY_PAGE_SIZE + 1 : 0;
    const pageEnd = totalResults ? Math.min(displayPage * OPPORTUNITY_PAGE_SIZE, totalResults) : 0;

    const summaryQuery = useQuery({
        queryKey: ['newOpportunitySummary', centerId, summaryParams],
        queryFn: () => getNewOpportunitySummaryAPI(centerId, authorization, summaryParams),
        enabled:
            isNewOpportunityEnabled &&
            !!centerId &&
            !!authorization &&
            !isInvalidDateRange,
    });
    const summaryData = summaryQuery.data?.data?.data || {};
    const hasSummarySnapshot = !!summaryData.summary;
    const summarySnapshotAt =
        Number(summaryQuery.dataUpdatedAt || 0) > 0
            ? dayjs(summaryQuery.dataUpdatedAt).format('YYYY.MM.DD HH:mm:ss')
            : '기준시각 확인 불가';
    const isSummaryInitialLoading =
        !hasSummarySnapshot && summaryQuery.isLoading;
    const isSummaryHardError =
        !hasSummarySnapshot && summaryQuery.isError;
    const isSummaryInvalidSuccess =
        !hasSummarySnapshot &&
        !summaryQuery.isLoading &&
        !summaryQuery.isError;
    const memberMatchQuery = useQuery({
        queryKey: ['newOpportunityMemberMatch', centerId, phoneDigits],
        queryFn: () => getNewOpportunityMemberMatchAPI(centerId, authorization, form.phoneNumber),
        enabled: isNewOpportunityEnabled && !!centerId && !!authorization && phoneDigits.length === 11,
        staleTime: 0,
    });
    const matchedMember = memberMatchQuery.data?.data?.matched ? memberMatchQuery.data?.data?.member : null;
    const isMatchedMemberReadOnly = isNewOpportunityRecordReadOnly(matchedMember);
    const displayMemberName = matchedMember?.name || form.memberName.trim();
    const isTimeRangeValid =
        dayjs(form.startTime).isValid() &&
        dayjs(form.endTime).isValid() &&
        dayjs(form.endTime).isAfter(dayjs(form.startTime));
    const formValid = !!(
        form.coachId &&
        displayMemberName &&
        phoneDigits.length === 11 &&
        form.categoryId &&
        form.startTime &&
        form.endTime &&
        isTimeRangeValid &&
        !isMatchedMemberReadOnly &&
        !memberMatchQuery.isLoading
    );
    const formValidationMessage = !form.coachId
        ? '담당강사를 선택해주세요.'
        : !form.categoryId
        ? '신규기회 유형을 선택해주세요.'
        : phoneDigits.length !== 11
        ? '회원 전화번호 11자리를 입력해주세요.'
        : memberMatchQuery.isLoading
        ? '기존 회원 여부를 확인하고 있습니다.'
        : isMatchedMemberReadOnly
        ? '탈퇴한 회원에게는 신규기회를 등록할 수 없습니다.'
        : !displayMemberName
        ? '회원 이름을 입력해주세요.'
        : !isTimeRangeValid
        ? '종료 시간을 시작 시간보다 늦게 설정해주세요.'
        : '';
    const summary: SummaryStats = summaryData.summary || {};
    const byCoach: SummaryStats[] = summaryData.byCoach || [];
    const byCategory: SummaryStats[] = summaryData.byCategory || [];
    const summaryRows: SummaryConversionRow[] = summaryData.rows || [];
    const convertedRows = summaryRows.filter((row) => row.converted);
    const isSummaryStale = hasSummarySnapshot && summaryQuery.isError;
    const isSummaryEmpty =
        hasSummarySnapshot &&
        !summaryQuery.isError &&
        !summaryQuery.isLoading &&
        Number(summary.opportunityCount || 0) === 0;
    const shouldShowSummaryPanels =
        hasSummarySnapshot && !isSummaryEmpty && (!summaryQuery.isError || isSummaryStale);
    const activeQuickRange = useMemo<QuickRange | null>(() => {
        const base = dayjs();
        const ranges: Record<QuickRange, { start: string; end: string }> = {
            month: {
                start: base.startOf('month').format('YYYY-MM-DD'),
                end: base.endOf('month').format('YYYY-MM-DD'),
            },
            lastMonth: {
                start: base.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                end: base.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
            },
            week: {
                start: base.subtract(6, 'day').format('YYYY-MM-DD'),
                end: base.format('YYYY-MM-DD'),
            },
        };
        return (Object.keys(ranges) as QuickRange[]).find((range) => {
            const rangeDates = ranges[range];
            return rangeDates.start === startDate && rangeDates.end === endDate;
        }) || null;
    }, [startDate, endDate]);

    useEffect(() => {
        setPage(1);
    }, [startDate, endDate, coachId, categoryId, keyword, journalEligibleFilter]);

    useEffect(() => {
        if (!opportunityQuery.isFetching && page > pageCount) {
            setPage(pageCount);
        }
    }, [opportunityQuery.isFetching, page, pageCount]);

    const createMutation = useMutation({
        mutationFn: () => {
            if (!formValid) {
                throw new Error('required');
            }
            const category = selectedCategory;
            const targetMemberName = matchedMember?.name || form.memberName.trim();
            return createNewOpportunityAPI(centerId, authorization, {
                coachId: form.coachId,
                memberId: matchedMember?.id || matchedMember?._id,
                memberName: targetMemberName,
                phoneNumber: formatNewOpportunityPhoneNumber(form.phoneNumber),
                categoryId: form.categoryId,
                name: `${category?.name || '신규기회'} - ${targetMemberName}`,
                startTime: fromDateTimeLocal(form.startTime),
                endTime: fromDateTimeLocal(form.endTime),
                memo: form.memo.trim(),
                isSendAlimtalkToMember: form.isSendAlimtalkToMember,
            });
        },
        onSuccess: () => {
            notify('신규기회가 등록되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['newOpportunities'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] });
            // 신규기회로 인입 회원이 생성될 수 있으므로 회원목록/통계도 무효화
            queryClient.invalidateQueries({ queryKey: ['publicMember'] });
            queryClient.invalidateQueries({ queryKey: ['memberStats'] });
            setForm((prev) => ({ ...prev, memberName: '', phoneNumber: '', memo: '' }));
            setIsCreateModalOpen(false);
        },
        onError: (error: any) => {
            // 클라이언트 측 필수값 검증 실패
            if (error?.message === 'required') {
                notify('필수값을 확인해주세요.');
                return;
            }
            // 중복 스케줄(이미 예약된 시간) - 백엔드 420/410
            const status = error?.response?.status;
            if (status === 409) {
                if (createAlert) {
                    createAlert(
                        '등록 불가',
                        error?.response?.data?.message || '탈퇴한 회원에게는 신규기회를 등록할 수 없습니다.',
                        true
                    );
                } else {
                    notify('탈퇴한 회원에게는 신규기회를 등록할 수 없습니다.');
                }
                return;
            }
            if (status === 410 || status === 420) {
                if (createAlert) createAlert('등록 불가', '해당 시간에는 이미 다른 일정이 등록되어 있어요.\n다른 시간을 선택해주세요.', true);
                else notify('해당 시간에는 이미 다른 일정이 등록되어 있어요. 다른 시간을 선택해주세요.');
                return;
            }
            // 그 외 오류
            if (createAlert) createAlert('등록 실패', '신규기회를 등록하지 못했어요. 잠시 후 다시 시도해주세요.', true);
            else notify('신규기회를 등록하지 못했어요. 잠시 후 다시 시도해주세요.');
        },
    });

    const createCategoryMutation = useMutation({
        mutationFn: () => createNewOpportunityCategoryAPI(centerId, authorization, { name: categoryName.trim() }),
        onSuccess: () => {
            setCategoryName('');
            queryClient.invalidateQueries({ queryKey: ['newOpportunityCategories'] });
        },
        onError: () => notify('카테고리를 추가하지 못했습니다.'),
    });

    const deleteCategoryMutation = useMutation({
        mutationFn: (category: Category) => deleteNewOpportunityCategoryAPI(centerId, getId(category), authorization),
        onSuccess: (_data, category) => {
            const deletedCategoryId = getId(category);
            setCategoryId((prev) => (prev === deletedCategoryId ? '' : prev));
            setForm((prev) => ({
                ...prev,
                categoryId: prev.categoryId === deletedCategoryId ? '' : prev.categoryId,
            }));
            queryClient.invalidateQueries({ queryKey: ['newOpportunityCategories'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunities'] });
            queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] });
        },
        onError: () => notify('카테고리를 삭제하지 못했습니다.'),
    });

    const toggleCategory = async (category: Category) => {
        await updateNewOpportunityCategoryAPI(centerId, getId(category), authorization, {
            isActive: category.isActive === false,
        });
        queryClient.invalidateQueries({ queryKey: ['newOpportunityCategories'] });
    };

    const deleteCategory = (category: Category) => {
        if (category.isDefault) {
            notify('기본 카테고리는 삭제할 수 없습니다. 필요하면 OFF로 숨겨주세요.');
            return;
        }
        if (!window.confirm(`${category.name} 카테고리를 삭제할까요? 기존 신규기회 기록은 유지됩니다.`)) {
            return;
        }
        deleteCategoryMutation.mutate(category);
    };

    const applyQuickRange = (range: QuickRange) => {
        const base = dayjs();
        if (range === 'lastMonth') {
            setStartDate(base.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
            setEndDate(base.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'));
            return;
        }
        if (range === 'week') {
            setStartDate(base.subtract(6, 'day').format('YYYY-MM-DD'));
            setEndDate(base.format('YYYY-MM-DD'));
            return;
        }
        setStartDate(base.startOf('month').format('YYYY-MM-DD'));
        setEndDate(base.endOf('month').format('YYYY-MM-DD'));
    };

    const handleStartTimeChange = (value: string) => {
        setForm((prev) => ({
            ...prev,
            startTime: value,
            endTime: dayjs(value).isValid() ? toDateTimeLocal(dayjs(value).add(50, 'minute')) : prev.endTime,
        }));
    };

    const handleRefresh = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            const results = await Promise.all([
                opportunityQuery.refetch(),
                summaryQuery.refetch(),
                categoryQuery.refetch(),
                coachQuery.refetch(),
            ]);
            if (results.some((result) => result.isError)) {
                notify('일부 정보를 새로고침하지 못했습니다. 오류 영역에서 다시 시도해주세요.');
                return;
            }
            setRefreshedAt(dayjs());
        } finally {
            setIsRefreshing(false);
        }
    };

    const applyListSearch = () => {
        setKeyword(keywordInput.trim());
    };

    const clearListSearch = () => {
        setKeywordInput('');
        setKeyword('');
    };

    const updateOpportunityMemo = async (row: OpportunityRow) => {
        if (isNewOpportunityRecordReadOnly(row)) {
            notify('탈퇴한 회원의 기록은 조회만 가능합니다.');
            return;
        }
        const rowId = getId(row);
        const originalMemo = row.memo || '';
        const nextMemo = memoDrafts[rowId] ?? originalMemo;

        if (!rowId || nextMemo === originalMemo || updatingMemoId) return;

        setUpdatingMemoId(rowId);
        try {
            await updateNewOpportunityAPI(centerId, rowId, authorization, { memo: nextMemo });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['newOpportunities'] }),
                queryClient.invalidateQueries({ queryKey: ['newOpportunitySummary'] }),
                queryClient.invalidateQueries({ queryKey: ['newOpportunityCalendar'] }),
            ]);
            setMemoDrafts((prev) => {
                const next = { ...prev };
                delete next[rowId];
                return next;
            });
        } catch {
            notify('메모 저장에 실패했습니다.');
        } finally {
            setUpdatingMemoId('');
        }
    };

    if (isFeatureFlagsLoading) {
        return (
            <main style={embedded ? { ...styles.page, ...styles.embeddedPage } : styles.page}>
                <EmptyState title="신규기회 기능을 확인하는 중입니다." />
            </main>
        );
    }

    if (!isNewOpportunityEnabled) {
        return <Navigate to="/centerReport/memberReviews" replace />;
    }

    return (
        <main
            className={`new-opportunity-page${embedded ? ' new-opportunity-embedded' : ''}`}
            style={embedded ? { ...styles.page, ...styles.embeddedPage } : styles.page}
        >
            <style>{`
                @media (max-width: 1280px) {
                    .new-opportunity-page:not(.new-opportunity-embedded) { padding: 28px 28px 64px 300px !important; }
                    .new-opportunity-kpis { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
                    .new-opportunity-kpi-card { grid-column: span 2; }
                    .new-opportunity-kpi-card:nth-last-child(-n + 2) { grid-column: span 3; }
                }
                .new-opportunity-kpi-card {
                    transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
                }
                .new-opportunity-kpi-button:hover {
                    border-color: #B9C9E7 !important;
                    box-shadow: 0 10px 24px rgba(26, 48, 93, 0.09) !important;
                    transform: translateY(-2px);
                }
                .new-opportunity-kpi-button:focus-visible {
                    outline: 3px solid rgba(61, 115, 221, 0.2);
                    outline-offset: 2px;
                }
                @media (max-width: 900px) {
                    .new-opportunity-page:not(.new-opportunity-embedded) { padding: 24px 18px 56px !important; }
                    .new-opportunity-header { align-items: flex-start !important; flex-direction: column; }
                    .new-opportunity-header-actions { width: 100%; flex-wrap: wrap; }
                    .new-opportunity-kpis { grid-template-columns: minmax(0, 1fr) !important; }
                    .new-opportunity-kpi-card { grid-column: auto !important; }
                    .new-opportunity-create-form { grid-template-columns: minmax(0, 1fr) !important; }
                    .new-opportunity-filter-control { flex: 1 1 180px !important; min-width: 0 !important; }
                }
                @media (max-width: 600px) {
                    .new-opportunity-page:not(.new-opportunity-embedded) { padding: 18px 12px 48px !important; }
                    .new-opportunity-clock-row { flex-wrap: wrap; }
                    .new-opportunity-clock-row > * { flex: 1 1 90px !important; }
                    .new-opportunity-clock-row > input { flex-basis: 100% !important; }
                    .new-opportunity-journal-modal { max-width: calc(100vw - 24px) !important; padding: 16px !important; }
                }
            `}</style>
            <section className="new-opportunity-header" style={styles.header}>
                <div>
                    <div style={styles.titleRow}>
                        <h1 style={styles.title}>신규기회</h1>
                        <div style={styles.eyebrow}>
                            <Target size={15} />
                            클로징 분석
                        </div>
                    </div>
                    <p style={styles.subtitle}>강사별 기회 생성부터 활성 수강회원 전환까지 한 화면에서 관리합니다.</p>
                </div>
                <div className="new-opportunity-header-actions" style={styles.headerActions}>
                    <button style={styles.primaryHeaderButton} type="button" onClick={() => setIsCreateModalOpen(true)}>
                        <Plus size={16} />
                        신규기회 등록
                    </button>
                    <button
                        style={{
                            ...styles.refreshButton,
                            opacity: isRefreshing || isInvalidDateRange ? 0.5 : 1,
                        }}
                        type="button"
                        disabled={isRefreshing || isInvalidDateRange}
                        onClick={handleRefresh}
                    >
                        <RefreshCw size={15} />
                        {isRefreshing ? '새로고침 중' : '새로고침'}
                    </button>
                    <span style={styles.refreshMeta}>{refreshedAt.format('HH:mm:ss')} 업데이트</span>
                </div>
            </section>

            <section style={styles.explainPanel}>
                <div style={styles.explainIcon}>
                    <Info size={18} />
                </div>
                <div style={styles.explainContent}>
                    <div style={styles.explainTitle}>수업 전 단계의 전환 후보 일정을 관리합니다.</div>
                    <div style={styles.explainText}>
                        OT·상담·체험 등을 캘린더에 기록하며 수강권 차감·정산과는 분리됩니다. 클로징률은 실제 활성 수강회원 전환을 기준으로 계산합니다.
                    </div>
                </div>
            </section>

            {journalEligibleFilter && (
                <section style={styles.evidenceNotice} aria-label="신규기회 일지 작성률 근거 조회 안내">
                    <BookOpen size={18} />
                    <div style={{ flex: 1 }}>
                        <div style={styles.evidenceNoticeTitle}>신규기회 일지 작성률 근거를 조회하고 있습니다.</div>
                        <div style={styles.evidenceNoticeText}>
                            시작 시각이 지났고 노쇼·삭제·취소가 아닌 정책 V1 일정을 대상으로 표시합니다. 작성 건은 ‘일지 보기’에서 읽기 전용으로 확인할 수 있습니다.
                        </div>
                    </div>
                    <button
                        type="button"
                        style={styles.evidenceClearButton}
                        onClick={() => {
                            setJournalEligibleFilter(false);
                            setEvidenceAsOf(undefined);
                            setPage(1);
                        }}
                    >
                        전체 신규기회 보기
                    </button>
                </section>
            )}

            {isCreateModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.createModal}>
                        <div style={styles.modalHeader}>
                            <PanelHeader title="신규기회 등록" caption="센터 관리자가 강사 대신 신규기회를 등록합니다." />
                            <button
                                style={styles.iconButton}
                                type="button"
                                onClick={() => setIsCreateModalOpen(false)}
                                aria-label="신규기회 등록 닫기"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={styles.modalNotice}>
                            신규기회는 수업 일정처럼 캘린더에 표시되지만 수강권과 차감, 정산에는 연결되지 않습니다. 상담 내용과 다음 액션은 메모에 남겨 강사와 센터 관리자가 함께 확인합니다.
                        </div>
                        <div className="new-opportunity-create-form" style={styles.formGrid}>
                            <Field label="담당강사">
                                <select style={styles.inputFull} value={form.coachId} onChange={(e) => setForm({ ...form, coachId: e.target.value })}>
                                    <option value="">담당강사 선택</option>
                                    {coaches.map((coach) => (
                                        <option key={coach.coachId} value={coach.coachId}>
                                            {coach.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="유형">
                                <select style={styles.inputFull} value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                                    <option value="">유형 선택</option>
                                    {activeCategories.map((category) => (
                                        <option key={getId(category)} value={getId(category)}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="회원 확인용 전화번호">
                                <input
                                    ref={phoneInputRef}
                                    style={styles.inputFull}
                                    placeholder="010-0000-0000"
                                    value={form.phoneNumber}
                                    maxLength={13}
                                    onChange={(e) => {
                                        const rawValue = e.target.value;
                                        const cursor = e.target.selectionStart ?? rawValue.length;
                                        const formatted = formatNewOpportunityPhoneNumber(rawValue);
                                        setForm({ ...form, phoneNumber: formatted });
                                        requestAnimationFrame(() => {
                                            const el = phoneInputRef.current;
                                            if (!el) return;
                                            const pos = calculateCursorAfterFormat(rawValue, cursor, formatted);
                                            try {
                                                el.setSelectionRange(pos, pos);
                                            } catch {
                                                // ignore
                                            }
                                        });
                                    }}
                                />
                            </Field>
                            {phoneDigits.length === 11 && (
                                <div
                                    style={{
                                        ...styles.memberMatchNotice,
                                        ...(isMatchedMemberReadOnly
                                            ? styles.memberMatchNoticeReadOnly
                                            : matchedMember
                                            ? styles.memberMatchNoticeFound
                                            : styles.memberMatchNoticeNew),
                                    }}
                                >
                                    {memberMatchQuery.isLoading
                                        ? '기존 회원 여부를 확인하고 있습니다.'
                                        : isMatchedMemberReadOnly
                                        ? `${matchedMember?.name || '이 회원'}님은 탈퇴한 회원으로, 기존 기록 조회만 가능하며 신규기회를 등록할 수 없습니다.`
                                        : matchedMember
                                        ? `${matchedMember.name || '이 회원'}님이 이미 센터에 등록되어 있습니다. 신규기회는 기존 회원에게 할당됩니다.`
                                        : '같은 전화번호의 센터 회원이 없어 신규 퍼블릭 회원으로 등록됩니다.'}
                                </div>
                            )}
                            {!matchedMember && (
                                <Field label="회원 이름">
                                    <input
                                        style={styles.inputFull}
                                        placeholder="회원 이름"
                                        value={form.memberName}
                                        maxLength={5}
                                        onChange={(e) => setForm({ ...form, memberName: limitMemberName(e.target.value) })}
                                    />
                                </Field>
                            )}
                            <div style={{ gridColumn: '1 / -1' }}>
                            <Field label="시작 (10분 단위)">
                                {(() => {
                                    const datePart = splitDateTimeLocal(form.startTime).date;
                                    const timePart = splitDateTimeLocal(form.startTime).time;
                                    const parts = splitClock(timePart);
                                    const updateParts = (next: typeof parts) =>
                                        handleStartTimeChange(
                                            joinDateTimeLocal(
                                                datePart || dayjs().format('YYYY-MM-DD'),
                                                joinClock(next),
                                            ),
                                        );
                                    return (
                                        <div className="new-opportunity-clock-row" style={{ display: 'flex', gap: 8 }}>
                                            <input
                                                style={{ ...styles.inputFull, flex: 1.4 }}
                                                type="date"
                                                value={datePart}
                                                onChange={(e) =>
                                                    handleStartTimeChange(
                                                        joinDateTimeLocal(e.target.value, timePart || '09:00'),
                                                    )
                                                }
                                            />
                                            <select
                                                style={{ ...styles.inputFull, flex: 1 }}
                                                value={parts.period}
                                                onChange={(e) =>
                                                    updateParts({ ...parts, period: e.target.value as 'AM' | 'PM' })
                                                }
                                            >
                                                <option value="AM">오전</option>
                                                <option value="PM">오후</option>
                                            </select>
                                            <select
                                                style={{ ...styles.inputFull, flex: 1 }}
                                                value={String(parts.hour12)}
                                                onChange={(e) =>
                                                    updateParts({ ...parts, hour12: Number(e.target.value) })
                                                }
                                            >
                                                {HOUR_OPTIONS_12.map((h) => (
                                                    <option key={h} value={String(h)}>
                                                        {String(h).padStart(2, '0')}시
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                style={{ ...styles.inputFull, flex: 1 }}
                                                value={String(parts.minute)}
                                                onChange={(e) =>
                                                    updateParts({ ...parts, minute: Number(e.target.value) })
                                                }
                                            >
                                                {MINUTE_OPTIONS_10.map((m) => (
                                                    <option key={m} value={String(m)}>
                                                        {String(m).padStart(2, '0')}분
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                })()}
                            </Field>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                            <Field label="종료 (10분 단위)">
                                {(() => {
                                    const datePart = splitDateTimeLocal(form.endTime).date;
                                    const timePart = splitDateTimeLocal(form.endTime).time;
                                    const parts = splitClock(timePart);
                                    const updateParts = (next: typeof parts) =>
                                        setForm({
                                            ...form,
                                            endTime: joinDateTimeLocal(
                                                datePart || dayjs().format('YYYY-MM-DD'),
                                                joinClock(next),
                                            ),
                                        });
                                    return (
                                        <div className="new-opportunity-clock-row" style={{ display: 'flex', gap: 8 }}>
                                            <input
                                                style={{ ...styles.inputFull, flex: 1.4 }}
                                                type="date"
                                                value={datePart}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        endTime: joinDateTimeLocal(e.target.value, timePart || '09:50'),
                                                    })
                                                }
                                            />
                                            <select
                                                style={{ ...styles.inputFull, flex: 1 }}
                                                value={parts.period}
                                                onChange={(e) =>
                                                    updateParts({ ...parts, period: e.target.value as 'AM' | 'PM' })
                                                }
                                            >
                                                <option value="AM">오전</option>
                                                <option value="PM">오후</option>
                                            </select>
                                            <select
                                                style={{ ...styles.inputFull, flex: 1 }}
                                                value={String(parts.hour12)}
                                                onChange={(e) =>
                                                    updateParts({ ...parts, hour12: Number(e.target.value) })
                                                }
                                            >
                                                {HOUR_OPTIONS_12.map((h) => (
                                                    <option key={h} value={String(h)}>
                                                        {String(h).padStart(2, '0')}시
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                style={{ ...styles.inputFull, flex: 1 }}
                                                value={String(parts.minute)}
                                                onChange={(e) =>
                                                    updateParts({ ...parts, minute: Number(e.target.value) })
                                                }
                                            >
                                                {MINUTE_OPTIONS_10.map((m) => (
                                                    <option key={m} value={String(m)}>
                                                        {String(m).padStart(2, '0')}분
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                })()}
                            </Field>
                            </div>
                        </div>
                        <Field label="공유 메모">
                            <textarea style={styles.textarea} placeholder="상담 내용, 다음 액션, 특이사항" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
                        </Field>
                        <label style={styles.notificationToggle}>
                            <input
                                type="checkbox"
                                checked={form.isSendAlimtalkToMember}
                                onChange={(e) => setForm({ ...form, isSendAlimtalkToMember: e.target.checked })}
                            />
                            <span>
                                앱 미사용 회원에게 알림톡 발송
                                <small style={styles.notificationToggleCaption}>회원 가입 후 앱에서 등록된 일정을 확인할 수 있습니다.</small>
                            </span>
                        </label>
                        {!formValid && (
                            <div
                                id="new-opportunity-form-guide"
                                role="status"
                                style={styles.formGuide}
                            >
                                {formValidationMessage}
                            </div>
                        )}
                        <div style={styles.modalFooter}>
                            <button style={styles.cancelButton} type="button" onClick={() => setIsCreateModalOpen(false)}>
                                취소
                            </button>
                            <button
                                style={{ ...styles.primaryButton, width: 180, opacity: formValid && !createMutation.isLoading ? 1 : 0.45 }}
                                type="button"
                                disabled={!formValid || createMutation.isLoading}
                                aria-describedby={!formValid ? 'new-opportunity-form-guide' : undefined}
                                title={formValidationMessage || '신규기회 등록'}
                                onClick={() => createMutation.mutate()}
                            >
                                <Plus size={17} />
                                {createMutation.isLoading ? '등록 중' : '등록'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {journalOpportunity && (
                <OpportunityJournalModal
                    centerId={centerId}
                    authorization={authorization}
                    opportunity={journalOpportunity}
                    onClose={() => setJournalOpportunity(null)}
                />
            )}

            <section className="new-opportunity-filter" style={styles.filterBar}>
                <div style={styles.filterHeading}>
                    <div style={styles.filterTitle}>
                        <Filter size={16} />
                        조회 조건
                    </div>
                    <div style={styles.filterScopeNote}>
                        선택한 조건이 분석과 목록에 함께 적용됩니다.
                    </div>
                </div>
                <div className="new-opportunity-filter-control" style={styles.compactFilterField}>
                    <span style={styles.filterInputLabel}>빠른 기간</span>
                    <div style={styles.quickRanges}>
                        <button
                            style={{ ...styles.quickButton, ...(activeQuickRange === 'month' ? styles.quickButtonActive : {}) }}
                            type="button"
                            aria-pressed={activeQuickRange === 'month'}
                            onClick={() => applyQuickRange('month')}
                        >
                            이번달
                        </button>
                        <button
                            style={{ ...styles.quickButton, ...(activeQuickRange === 'lastMonth' ? styles.quickButtonActive : {}) }}
                            type="button"
                            aria-pressed={activeQuickRange === 'lastMonth'}
                            onClick={() => applyQuickRange('lastMonth')}
                        >
                            지난달
                        </button>
                        <button
                            style={{ ...styles.quickButton, ...(activeQuickRange === 'week' ? styles.quickButtonActive : {}) }}
                            type="button"
                            aria-pressed={activeQuickRange === 'week'}
                            onClick={() => applyQuickRange('week')}
                        >
                            최근 7일
                        </button>
                    </div>
                </div>
                <label className="new-opportunity-filter-control" style={styles.compactFilterField}>
                    <span style={styles.filterInputLabel}>시작일</span>
                    <input
                        style={styles.input}
                        type="date"
                        aria-label="조회 시작일"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </label>
                <label className="new-opportunity-filter-control" style={styles.compactFilterField}>
                    <span style={styles.filterInputLabel}>종료일 (포함)</span>
                    <input
                        style={styles.input}
                        type="date"
                        aria-label="조회 종료일 포함"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </label>
                <label className="new-opportunity-filter-control" style={styles.compactFilterField}>
                    <span style={styles.filterInputLabel}>담당 강사</span>
                    <select
                        style={styles.input}
                        aria-label="담당 강사 필터"
                        value={coachId}
                        onChange={(e) => setCoachId(e.target.value)}
                    >
                        <option value="">전체 강사</option>
                        {coaches.map((coach) => (
                            <option key={coach.coachId} value={coach.coachId}>
                                {coach.name}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="new-opportunity-filter-control" style={styles.compactFilterField}>
                    <span style={styles.filterInputLabel}>유형</span>
                    <select
                        style={styles.input}
                        aria-label="신규기회 유형 필터"
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                    >
                        <option value="">전체 유형</option>
                        {categories.map((category) => (
                            <option key={getId(category)} value={getId(category)}>
                                {category.name}
                                {category.isActive === false ? ' (비활성)' : ''}
                            </option>
                        ))}
                    </select>
                </label>
            </section>

            {isInvalidDateRange && (
                <div style={styles.queryError} role="alert">
                    조회 종료일은 시작일과 같거나 뒤여야 합니다.
                </div>
            )}
            {(categoryQuery.isError || coachQuery.isError) && (
                <QueryErrorState
                    title="필터 정보를 일부 불러오지 못했습니다."
                    onRetry={() => {
                        categoryQuery.refetch();
                        coachQuery.refetch();
                    }}
                    compact
                />
            )}

            {!isInvalidDateRange && isSummaryHardError && (
                <QueryErrorState
                    title="분석 지표를 불러오지 못했습니다. 기존 수치를 0으로 표시하지 않습니다."
                    onRetry={() => summaryQuery.refetch()}
                />
            )}
            {!isInvalidDateRange && isSummaryInvalidSuccess && (
                <QueryErrorState
                    title="분석 지표 응답을 확인할 수 없습니다. 기존 수치를 0으로 표시하지 않습니다."
                    onRetry={() => summaryQuery.refetch()}
                />
            )}
            {!isInvalidDateRange && isSummaryStale && (
                <QueryErrorState
                    title={`분석 지표 최신화에 실패했습니다. 마지막 성공 기준시각 ${summarySnapshotAt} 데이터를 표시합니다.`}
                    onRetry={() => summaryQuery.refetch()}
                />
            )}
            {!isInvalidDateRange && isSummaryInitialLoading && (
                <EmptyState title="분석 지표를 불러오는 중입니다." />
            )}
            {!isInvalidDateRange && isSummaryEmpty && (
                <EmptyState title="조회 범위에 분석할 신규기회가 없습니다. 실제 빈 결과입니다." />
            )}

            {!isInvalidDateRange && shouldShowSummaryPanels && (
            <>
            <section
                className="new-opportunity-kpis"
                style={styles.kpiGrid}
                aria-busy={summaryQuery.isLoading || summaryQuery.isFetching}
            >
                <Kpi icon={<UsersRound size={20} />} label="신규기회 회원" value={`${formatCount(summary.leadMemberCount)}명`} sub={`${formatCount(summary.opportunityCount)}건`} />
                <Kpi
                    icon={<CheckCircle2 size={20} />}
                    label="클로징 회원"
                    value={`${formatCount(summary.closedMemberCount)}명`}
                    sub="클릭해 성공 목록 보기"
                    tone="green"
                    onClick={() => closingListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
                <Kpi
                    icon={<BarChart3 size={20} />}
                    label="클로징률"
                    value={formatRate(summary.closingRate, summary.rateUnit)}
                    sub={summary.closedMemberCount ? `${formatAverage(summary.avgOpportunityOrderToClose)} 후 전환` : '전환 대기'}
                    tone="blue"
                />
                <Kpi
                    icon={<BookOpen size={20} />}
                    label="신규기회 일지 작성률"
                    value={formatTargetRate(
                        summary.journalWriteRate,
                        summary.journalWriteTarget,
                        summary.rateUnit
                    )}
                    sub={
                        summary.journalWriteTarget
                            ? `작성 ${formatCount(summary.journalWriteWritten)} / 대상 ${formatCount(
                                  summary.journalWriteTarget
                              )}건`
                            : '작성 대상 없음'
                    }
                    tone="purple"
                    onClick={() => {
                        setJournalEligibleFilter(true);
                        setEvidenceAsOf(
                            summaryData.journalEligibilityEvaluatedAt ||
                                summaryData.asOf ||
                                undefined
                        );
                        setPage(1);
                        if (typeof opportunityListRef.current?.scrollIntoView === 'function') {
                            opportunityListRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            });
                        }
                    }}
                />
                <Kpi icon={<Clock3 size={20} />} label="회원당 평균 기회" value={formatAverage(summary.avgOpportunitiesPerMember)} sub="중복 기회 포함" tone="purple" />
            </section>

            <section ref={closingListRef} style={{ ...styles.panel, ...styles.closingSuccessPanel }}>
                <ClosingSuccessPanel rows={convertedRows} loading={summaryQuery.isLoading || summaryQuery.isFetching} />
            </section>

            <section style={styles.coachClosingPanel}>
                <PanelHeader title="강사별 클로징" caption="신규기회 회원 수, 생성 건수, 활성 수강회원 전환율을 강사별로 비교합니다." />
                <CoachClosingBoard items={byCoach} convertedRows={convertedRows} />
            </section>

            <section style={styles.analyticsGrid}>
                <div style={styles.panel}>
                    <PanelHeader title="유형별 반응" caption="카테고리별 기회 수와 전환율" />
                    <RankingList items={byCategory.slice(0, 5)} labelKey="categoryName" emptyText="유형별 데이터가 없습니다." />
                </div>
            </section>
            </>
            )}

            <section style={styles.workGrid}>
                <div style={styles.panel}>
                    <PanelHeader title="카테고리 관리" caption="센터 운영 방식에 맞춰 유형을 추가합니다." />
                    <div style={styles.inline}>
                        <input style={styles.inputGrow} placeholder="카테고리명" value={categoryName} onChange={(e) => setCategoryName(e.target.value)} />
                        <button
                            style={styles.secondaryButton}
                            type="button"
                            disabled={!categoryName.trim() || createCategoryMutation.isLoading}
                            onClick={() => categoryName.trim() && createCategoryMutation.mutate()}
                        >
                            추가
                        </button>
                    </div>
                    <div style={styles.categoryList}>
                        {categories.map((category) => {
                            const active = category.isActive !== false;
                            return (
                                <div
                                    key={getId(category)}
                                    style={{
                                        ...styles.categoryItem,
                                        ...(active ? styles.categoryChipActive : styles.categoryChipInactive),
                                    }}
                                >
                                    <button style={styles.categoryToggleButton} type="button" onClick={() => toggleCategory(category)}>
                                        {category.name}
                                        <span style={styles.categoryChipState}>{active ? 'ON' : 'OFF'}</span>
                                    </button>
                                    {!category.isDefault && (
                                        <button
                                            style={styles.categoryDeleteButton}
                                            type="button"
                                            title="카테고리 삭제"
                                            disabled={deleteCategoryMutation.isLoading}
                                            onClick={() => deleteCategory(category)}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section ref={opportunityListRef} style={styles.panel}>
                <div style={styles.tableHeader}>
                    <PanelHeader
                        title="신규기회 목록"
                        caption={
                            opportunityQuery.isFetching && !opportunityQuery.isLoading
                                ? `${formatCount(totalResults)}건 · 최신 정보 확인 중`
                                : `${formatCount(totalResults)}건`
                        }
                    />
                    <div style={styles.tableHeaderActions}>
                        {journalEligibleFilter && (
                            <button
                                type="button"
                                style={styles.activeListFilter}
                                onClick={() => {
                                    setJournalEligibleFilter(false);
                                    setEvidenceAsOf(undefined);
                                    setPage(1);
                                }}
                                aria-label="신규기회 일지 작성률 대상 필터 해제"
                            >
                                <BookOpen size={14} />
                                작성률 대상만
                                <X size={14} />
                            </button>
                        )}
                        <div style={{ ...styles.searchBox, ...styles.listSearchBox }}>
                            <Search size={17} />
                            <input
                                style={styles.searchInput}
                                value={keywordInput}
                                aria-label="신규기회 목록 검색어"
                                placeholder="회원 이름, 전화번호, 강사, 유형, 메모 검색"
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        applyListSearch();
                                    }
                                }}
                            />
                            {(keywordInput.trim() || keyword.trim()) && (
                                <button type="button" style={styles.searchClearButton} onClick={clearListSearch} title="검색어 지우기">
                                    <X size={15} />
                                </button>
                            )}
                        </div>
                        <button type="button" style={styles.listSearchButton} onClick={applyListSearch}>
                            검색
                        </button>
                    </div>
                </div>
                <div style={styles.tableWrap}>
                    <table style={styles.table} aria-busy={opportunityQuery.isLoading || opportunityQuery.isFetching}>
                        <caption style={styles.srOnly}>
                            신규기회 일시, 강사, 회원, 유형, 신규기회 일지 작성 및 전환 정보
                        </caption>
                        <thead>
                            <tr>
                                <Th>일시</Th>
                                <Th>강사</Th>
                                <Th>회원</Th>
                                <Th>유형</Th>
                                <Th>신규기회 일지</Th>
                                <Th>전환</Th>
                                <Th>메모</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {opportunities.map((row) => {
                                const rowId = getId(row);
                                return (
                                    <tr key={rowId}>
                                        <Td>
                                            <div style={styles.dateCell}>
                                                <CalendarDays size={14} />
                                                <span>
                                                    {dayjs(row.startTime).isValid()
                                                        ? dayjs(row.startTime).format('MM.DD HH:mm')
                                                        : '-'}
                                                </span>
                                            </div>
                                            <div style={styles.subText}>
                                                {dayjs(row.endTime).isValid()
                                                    ? `${dayjs(row.endTime).format('HH:mm')} 종료`
                                                    : '종료 시간 미정'}
                                            </div>
                                        </Td>
                                        <Td>
                                            <div style={styles.nameCell}>
                                                <UserRound size={14} />
                                                {row.coachName || '-'}
                                            </div>
                                        </Td>
                                        <Td>
                                            <div style={styles.memberName}>{row.memberName || '-'}</div>
                                            <div style={styles.subText}>{formatNewOpportunityPhoneNumber(row.memberPhoneNumber) || '-'}</div>
                                            {isNewOpportunityRecordReadOnly(row) && (
                                                <div style={styles.readOnlyBadge}>탈퇴 · 조회 전용</div>
                                            )}
                                        </Td>
                                        <Td>
                                            <span
                                                style={styles.typeBadge}
                                                title={row.displayName || row.categoryName || '유형 없음'}
                                            >
                                                {row.displayName || row.categoryName || '-'}
                                            </span>
                                        </Td>
                                        <Td>
                                            {row.hasWorkoutJournal === true ? (
                                                <button
                                                    type="button"
                                                    style={styles.journalViewButton}
                                                    onClick={() => setJournalOpportunity(row)}
                                                    aria-label={`${row.displayName || row.categoryName || '수업'} 신규기회 일지 읽기 전용으로 보기`}
                                                >
                                                    <BookOpen size={14} />
                                                    일지 보기
                                                </button>
                                            ) : (
                                                <span
                                                    style={
                                                        row.hasWorkoutJournal === false
                                                            ? styles.journalEmptyButton
                                                            : styles.journalUnknownBadge
                                                    }
                                                >
                                                    <BookOpen size={14} />
                                                    {getJournalEmptyLabel(row)}
                                                </span>
                                            )}
                                        </Td>
                                        <Td>
                                            {row.converted ? (
                                                <div>
                                                    <span style={styles.conversionBadge}>
                                                        <CheckCircle2 size={15} />
                                                        결제 전환
                                                    </span>
                                                    <div style={styles.subText}>
                                                        {formatConversionSource(row.conversionSourceType)} · {dayjs(row.convertedAt || row.paymentDate).format('MM.DD HH:mm')}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span style={styles.waitingBadge}>전환 대기</span>
                                            )}
                                        </Td>
                                        <Td>
                                            <div style={styles.memoEditCell}>
                                                <textarea
                                                    style={{
                                                        ...styles.memoTextarea,
                                                        ...(isNewOpportunityRecordReadOnly(row)
                                                            ? styles.readOnlyTextarea
                                                            : {}),
                                                    }}
                                                    placeholder="메모"
                                                    value={memoDrafts[rowId] ?? row.memo ?? ''}
                                                    disabled={isNewOpportunityRecordReadOnly(row)}
                                                    onChange={(e) => setMemoDrafts((prev) => ({ ...prev, [rowId]: e.target.value }))}
                                                />
                                                {!isNewOpportunityRecordReadOnly(row) && (
                                                    <button
                                                        style={{
                                                            ...styles.memoSaveButton,
                                                            opacity: updatingMemoId || (memoDrafts[rowId] ?? row.memo ?? '') === (row.memo || '') ? 0.45 : 1,
                                                        }}
                                                        type="button"
                                                        disabled={!!updatingMemoId || (memoDrafts[rowId] ?? row.memo ?? '') === (row.memo || '')}
                                                        onClick={() => updateOpportunityMemo(row)}
                                                    >
                                                        {updatingMemoId === rowId ? '저장 중' : '저장'}
                                                    </button>
                                                )}
                                            </div>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {!isInvalidDateRange && opportunityQuery.isLoading && (
                        <EmptyState title="신규기회 목록을 불러오는 중입니다." />
                    )}
                    {!isInvalidDateRange && opportunityQuery.isError && (
                        <QueryErrorState
                            title="신규기회 목록을 불러오지 못했습니다."
                            onRetry={() => opportunityQuery.refetch()}
                            compact
                        />
                    )}
                    {!opportunityQuery.isLoading &&
                        !opportunityQuery.isError &&
                        !isInvalidDateRange &&
                        opportunities.length === 0 && (
                            <EmptyState title="조건에 맞는 신규기회가 없습니다." />
                        )}
                </div>
                {!isInvalidDateRange && !opportunityQuery.isError && totalResults > 0 && (
                <div style={styles.paginationBar}>
                    <span style={styles.paginationMeta}>
                        {formatCount(totalResults)}건 중 {formatCount(pageStart)}-{formatCount(pageEnd)}
                    </span>
                    <div style={styles.paginationControls}>
                        <button
                            style={{ ...styles.paginationButton, opacity: page <= 1 || opportunityQuery.isFetching ? 0.45 : 1 }}
                            type="button"
                            disabled={page <= 1 || opportunityQuery.isFetching}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        >
                            이전
                        </button>
                        <span style={styles.paginationPage}>
                            {formatCount(displayPage)} / {formatCount(pageCount)}
                        </span>
                        <button
                            style={{ ...styles.paginationButton, opacity: page >= pageCount || opportunityQuery.isFetching ? 0.45 : 1 }}
                            type="button"
                            disabled={page >= pageCount || opportunityQuery.isFetching}
                            onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                        >
                            다음
                        </button>
                    </div>
                </div>
                )}
            </section>
        </main>
    );
};

const OpportunityJournalModal = ({
    centerId,
    authorization,
    opportunity,
    onClose,
}: {
    centerId: string;
    authorization: string;
    opportunity: OpportunityRow;
    onClose: () => void;
}) => {
    const opportunityId = getId(opportunity);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const journalQuery = useQuery({
        queryKey: ['newOpportunityJournal', centerId, opportunityId],
        queryFn: () => getNewOpportunityJournalAPI(centerId, opportunityId, authorization),
        enabled: !!centerId && !!opportunityId && !!authorization,
        retry: false,
    });
    const response = journalQuery.data?.data;
    const resolvedOpportunity = response?.opportunity || opportunity;
    const modalTitle = `${
        resolvedOpportunity.displayName || resolvedOpportunity.categoryName || '수업'
    } 운동일지`;

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(
                dialogRef.current?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ) || []
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            previouslyFocused?.focus();
        };
    }, []);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div
                ref={dialogRef}
                className="new-opportunity-journal-modal"
                style={styles.journalModal}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="new-opportunity-journal-modal-title"
            >
                <div style={styles.modalHeader}>
                    <div>
                        <h2 id="new-opportunity-journal-modal-title" style={styles.panelTitle}>
                            {modalTitle}
                        </h2>
                        <p style={styles.panelCaption}>
                            관리자 화면에서는 조회만 가능하며 작성·수정·삭제는 제공하지 않습니다.
                        </p>
                    </div>
                    <button
                        ref={closeButtonRef}
                        style={styles.iconButton}
                        type="button"
                        onClick={onClose}
                        aria-label="운동일지 닫기"
                    >
                        <X size={20} />
                    </button>
                </div>
                <NewOpportunityJournalReadOnly
                    journal={response?.journal || null}
                    isLoading={
                        journalQuery.isLoading ||
                        (journalQuery.isFetching && !journalQuery.data)
                    }
                    isError={journalQuery.isError}
                    error={journalQuery.error}
                    onRetry={() => journalQuery.refetch()}
                    displayName={resolvedOpportunity.displayName || resolvedOpportunity.categoryName}
                    coachName={resolvedOpportunity.coachName || opportunity.coachName}
                    startTime={resolvedOpportunity.startTime || opportunity.startTime}
                    endTime={resolvedOpportunity.endTime || opportunity.endTime}
                />
            </div>
        </div>
    );
};

const Kpi = ({
    icon,
    label,
    value,
    sub,
    tone = 'navy',
    onClick,
}: {
    icon: ReactNode;
    label: string;
    value: string;
    sub: string;
    tone?: 'navy' | 'green' | 'blue' | 'purple';
    onClick?: () => void;
}) => {
    const toneStyle = {
        navy: { color: '#1A305D', bg: '#F8FAFC' },
        green: { color: '#047857', bg: '#ECFDF5' },
        blue: { color: '#1D4ED8', bg: '#EFF6FF' },
        purple: { color: '#6D28D9', bg: '#F5F3FF' },
    }[tone];
    const body = (
        <>
            <div style={{ ...styles.kpiIcon, color: toneStyle.color, background: toneStyle.bg }}>{icon}</div>
            <div>
                <div style={styles.kpiLabel}>{label}</div>
                <div style={styles.kpiValue}>{value}</div>
                <div style={styles.kpiSub}>{sub}</div>
            </div>
        </>
    );

    if (onClick) {
        return (
            <button
                type="button"
                className="new-opportunity-kpi-card new-opportunity-kpi-button"
                style={{ ...styles.kpi, ...styles.kpiButton }}
                onClick={onClick}
            >
                {body}
            </button>
        );
    }

    return (
        <div className="new-opportunity-kpi-card" style={styles.kpi}>
            {body}
        </div>
    );
};

const PanelHeader = ({ title, caption }: { title: string; caption?: string }) => (
    <div style={styles.panelHeader}>
        <div>
            <h2 style={styles.panelTitle}>{title}</h2>
            {caption && <p style={styles.panelCaption}>{caption}</p>}
        </div>
    </div>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <label style={styles.field}>
        <span style={styles.fieldLabel}>{label}</span>
        {children}
    </label>
);

const RankingList = ({ items, labelKey, emptyText }: { items: SummaryStats[]; labelKey: 'coachName' | 'categoryName'; emptyText: string }) => {
    if (!items.length) return <EmptyState title={emptyText} compact />;
    return (
        <div style={styles.rankingList}>
            {items.map((item, index) => (
                <div key={`${labelKey}-${item[labelKey]}-${index}`} style={styles.rankingRow}>
                    <div style={styles.rankBadge}>{index + 1}</div>
                    <div style={styles.rankMain}>
                        <div style={styles.rankName}>{item[labelKey] || '이름 없음'}</div>
                        <div style={styles.rankMeta}>
                            {formatCount(item.leadMemberCount)}명 · {formatCount(item.opportunityCount)}건
                        </div>
                        {(item.journalWriteTarget || item.journalWriteWritten) && (
                            <div style={styles.rankJournalMeta}>
                                신규기회 일지 {formatCount(item.journalWriteWritten)} / {formatCount(item.journalWriteTarget)}건
                                {' · '}
                                {formatRate(item.journalWriteRate, item.rateUnit)}
                            </div>
                        )}
                    </div>
                    <div style={styles.rankRate}>
                        {formatRate(item.closingRate, item.rateUnit)}
                    </div>
                </div>
            ))}
        </div>
    );
};

const ClosingSuccessPanel = ({ rows, loading }: { rows: SummaryConversionRow[]; loading: boolean }) => {
    const [keyword, setKeyword] = useState('');
    const [page, setPage] = useState(1);
    const showCenterColumn = useMemo(() => new Set(rows.map((row) => row.centerId).filter(Boolean)).size > 1, [rows]);
    const filteredRows = useMemo(() => {
        const sorted = [...rows].sort(
            (a, b) => dayjs(b.convertedAt || b.paymentDate).valueOf() - dayjs(a.convertedAt || a.paymentDate).valueOf()
        );
        const normalizedKeyword = keyword.trim().toLowerCase();
        if (!normalizedKeyword) return sorted;

        return sorted.filter((row) => {
            const categoryText = row.opportunityCategoryNames?.join(' ') || row.latestCategoryName || row.categoryName || '';
            const phoneText = formatNewOpportunityPhoneNumber(row.memberPhoneNumber);
            return [
                row.centerName,
                row.memberName,
                row.memberPhoneNumber,
                phoneText,
                row.coachName,
                categoryText,
                row.conversionSourceName,
                formatConversionSource(row.conversionSourceType),
            ]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(normalizedKeyword));
        });
    }, [keyword, rows]);
    const pageCount = Math.max(Math.ceil(filteredRows.length / CLOSING_PAGE_SIZE), 1);
    const displayPage = Math.min(page, pageCount);
    const pageRows = filteredRows.slice((displayPage - 1) * CLOSING_PAGE_SIZE, displayPage * CLOSING_PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [keyword, rows.length]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    return (
        <>
            <div style={styles.closingSuccessHeader}>
                <PanelHeader title="클로징 성공 회원" caption="실제 PT/그룹 수강권 결제 기준으로 전환된 회원입니다." />
                <div style={styles.closingSuccessCounter}>
                    {formatCount(filteredRows.length)}명
                </div>
            </div>
            <div style={styles.closingSuccessToolbar}>
                <div style={{ ...styles.searchBox, ...styles.closingSearchBox }}>
                    <Search size={17} />
                    <input
                        style={styles.searchInput}
                        value={keyword}
                        placeholder="회원, 전화, 강사, 유형, 수강권 검색"
                        onChange={(event) => setKeyword(event.target.value)}
                    />
                </div>
            </div>
            <div style={styles.tableWrap}>
                <table style={{ ...styles.table, ...styles.closingTable }}>
                    <thead>
                        <tr>
                            <Th>전환일</Th>
                            {showCenterColumn && <Th>센터</Th>}
                            <Th>회원</Th>
                            <Th>담당강사</Th>
                            <Th>신규기회</Th>
                            <Th>등록 수강권</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row, rowIndex) => {
                            const categoryText = row.opportunityCategoryNames?.join(', ') || row.latestCategoryName || row.categoryName || '-';
                            const conversionSourceName = row.conversionSourceName || formatConversionSource(row.conversionSourceType);
                            const convertedAt = row.convertedAt || row.paymentDate;
                            // 같은 회원의 여러 신규기회가 한 결제로 함께 전환될 수 있어
                            // 회원/전환일만으로는 React key가 중복된다. 행의 기회 정보와
                            // 현재 페이지 내 순번까지 포함해 월 전환 시 이전 DOM 행이 남지 않게 한다.
                            const rowKey = [
                                row.centerId,
                                row.memberId || row.memberName,
                                convertedAt || row.firstOpportunityAt,
                                row.coachId || row.coachName,
                                row.firstOpportunityAt,
                                categoryText,
                                row.conversionSourceType,
                                row.conversionSourceName,
                                row.opportunityOrderToClose,
                                rowIndex,
                            ]
                                .map((value) => String(value ?? ''))
                                .join('::');
                            return (
                                <tr key={rowKey}>
                                    <Td>
                                        <div style={styles.dateCell}>
                                            <CheckCircle2 size={14} />
                                            <span>{formatDateTime(convertedAt, 'MM.DD HH:mm')}</span>
                                        </div>
                                    </Td>
                                    {showCenterColumn && (
                                        <Td>
                                            <div style={styles.nameCell}>{row.centerName || '-'}</div>
                                        </Td>
                                    )}
                                    <Td>
                                        <div style={styles.memberName}>{row.memberName || '-'}</div>
                                        <div style={styles.subText}>{formatNewOpportunityPhoneNumber(row.memberPhoneNumber) || '-'}</div>
                                    </Td>
                                    <Td>
                                        <div style={styles.nameCell}>
                                            <UserRound size={14} />
                                            {row.coachName || '-'}
                                        </div>
                                    </Td>
                                    <Td>
                                        <span style={styles.typeBadge}>{categoryText}</span>
                                        <div style={styles.subText}>
                                            {formatDateTime(row.firstOpportunityAt, 'MM.DD HH:mm')} 시작 · {formatCount(row.opportunityCount)}건
                                            {row.opportunityOrderToClose ? ` 중 ${formatCount(row.opportunityOrderToClose)}번째 전환` : ''}
                                        </div>
                                    </Td>
                                    <Td>
                                        <span style={styles.conversionBadge}>
                                            <CheckCircle2 size={15} />
                                            {conversionSourceName}
                                        </span>
                                        <div style={styles.subText}>{formatConversionSource(row.conversionSourceType)} 결제</div>
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {loading && <EmptyState title="클로징 성공 회원을 불러오는 중입니다." />}
                {!loading && rows.length === 0 && <EmptyState title="아직 클로징 성공 회원이 없습니다." />}
                {!loading && rows.length > 0 && filteredRows.length === 0 && <EmptyState title="검색 조건에 맞는 클로징 성공 회원이 없습니다." />}
            </div>
            {filteredRows.length > CLOSING_PAGE_SIZE && (
                <div style={styles.paginationBar}>
                    <span style={styles.paginationMeta}>
                        {formatCount(filteredRows.length)}명 중 {formatCount((displayPage - 1) * CLOSING_PAGE_SIZE + 1)}-
                        {formatCount(Math.min(displayPage * CLOSING_PAGE_SIZE, filteredRows.length))}
                    </span>
                    <div style={styles.paginationControls}>
                        <button
                            style={{ ...styles.paginationButton, opacity: displayPage <= 1 ? 0.45 : 1 }}
                            type="button"
                            disabled={displayPage <= 1}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        >
                            이전
                        </button>
                        <span style={styles.paginationPage}>
                            {formatCount(displayPage)} / {formatCount(pageCount)}
                        </span>
                        <button
                            style={{ ...styles.paginationButton, opacity: displayPage >= pageCount ? 0.45 : 1 }}
                            type="button"
                            disabled={displayPage >= pageCount}
                            onClick={() => setPage((prev) => Math.min(prev + 1, pageCount))}
                        >
                            다음
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

const CoachClosingBoard = ({ items, convertedRows }: { items: SummaryStats[]; convertedRows: SummaryConversionRow[] }) => {
    if (!items.length) return <EmptyState title="강사별 데이터가 없습니다." compact />;

    const sorted = [...items].sort((a, b) => (b.leadMemberCount || 0) - (a.leadMemberCount || 0));

    return (
        <div style={styles.coachClosingBoard}>
            <div style={styles.coachClosingHeaderRow}>
                <span>강사</span>
                <span>클로징률</span>
                <span>전환 결과</span>
            </div>
            {sorted.slice(0, 8).map((item, index) => {
                const leadCount = item.leadMemberCount || 0;
                const closedCount = item.closedMemberCount || 0;
                const opportunityCount = item.opportunityCount || 0;
                const closingPercent = Math.max(
                    0,
                    Math.min(
                        normalizeRatePercent(item.closingRate, item.rateUnit) || 0,
                        100
                    )
                );
                const hasClosingProgress = closingPercent > 0;
                const coachConvertedRows = convertedRows.filter((row) => row.coachId === item.coachId);
                return (
                    <div key={`${item.coachId || item.coachName}-${index}`} style={styles.coachClosingRow}>
                        <div style={styles.coachClosingIdentity}>
                            <div style={styles.coachRankBadge}>{index + 1}</div>
                            <div>
                                <div style={styles.coachClosingName}>{item.coachName || '이름 없음'}</div>
                                <div style={styles.coachClosingSub}>
                                    {formatCount(leadCount)}명 대상 · {formatCount(opportunityCount)}건 생성
                                </div>
                                <div style={styles.coachJournalSub}>
                                    신규기회 일지 {formatCount(item.journalWriteWritten)} / {formatCount(item.journalWriteTarget)}건
                                    {' · '}
                                    {formatRate(item.journalWriteRate, item.rateUnit)}
                                </div>
                            </div>
                        </div>
                        <div style={styles.coachClosingOpportunity}>
                            <div style={styles.coachClosingBarHeader}>
                                <span>활성 수강회원 전환율</span>
                                <strong style={styles.coachClosingBarRate}>
                                    {formatRate(item.closingRate, item.rateUnit)}
                                </strong>
                            </div>
                            <div style={styles.coachProgressTrack}>
                                <span
                                    style={{
                                        ...styles.coachProgressFill,
                                        width: `${closingPercent}%`,
                                        minWidth: hasClosingProgress ? 4 : 0,
                                        background: hasClosingProgress ? '#3D73DD' : 'transparent',
                                    }}
                                />
                            </div>
                            <div style={styles.coachClosingMeta}>
                                전환 기준: 활성 수강회원
                            </div>
                        </div>
                        <div style={styles.coachClosingRateWrap}>
                            <div style={styles.coachClosingResultCard}>
                                <div style={styles.coachClosingResult}>{formatCount(closedCount)}명 전환</div>
                                <div style={styles.coachClosingSub}>
                                    {item.avgOpportunityOrderToClose ? `${formatAverage(item.avgOpportunityOrderToClose)} 후 전환` : '전환 대기'}
                                </div>
                                {coachConvertedRows.length > 0 && (
                                    <div style={styles.convertedMemberList}>
                                        {coachConvertedRows.slice(0, 4).map((row) => (
                                            <span key={`${row.memberId}-${row.convertedAt || row.paymentDate}`} style={styles.convertedMemberPill}>
                                                {row.memberName || '이름 없음'}
                                            </span>
                                        ))}
                                        {coachConvertedRows.length > 4 && (
                                            <span style={styles.convertedMemberMore}>+{formatCount(coachConvertedRows.length - 4)}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const EmptyState = ({ title, compact = false }: { title: string; compact?: boolean }) => (
    <div style={{ ...styles.emptyState, padding: compact ? '18px 0' : '44px 0' }}>
        <Target size={compact ? 20 : 28} />
        <span>{title}</span>
    </div>
);

const QueryErrorState = ({
    title,
    onRetry,
    compact = false,
}: {
    title: string;
    onRetry: () => void;
    compact?: boolean;
}) => (
    <div
        style={{
            ...styles.queryError,
            marginBottom: compact ? 0 : 16,
            padding: compact ? '18px 14px' : '16px 18px',
        }}
        role="alert"
    >
        <span>{title}</span>
        <button type="button" style={styles.queryRetryButton} onClick={onRetry}>
            <RefreshCw size={14} />
            다시 시도
        </button>
    </div>
);

const Th = ({ children }: any) => <th style={styles.th}>{children}</th>;
const Td = ({ children }: any) => <td style={styles.td}>{children}</td>;

const styles: Record<string, CSSProperties> = {
    page: { padding: '40px 48px 80px 340px', background: '#F5F7FA', minHeight: '100vh', color: '#111827' },
    embeddedPage: { padding: 0, background: 'transparent', minHeight: 'auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 28, marginBottom: 16 },
    titleRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    eyebrow: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 999, background: '#ECF1FC', color: '#1D4ED8', fontSize: 14, lineHeight: '20px', fontWeight: 850 },
    title: { margin: 0, fontSize: 34, lineHeight: '42px', color: '#1A305D', letterSpacing: 0, fontWeight: 900 },
    subtitle: { margin: '8px 0 0', color: '#5E7093', fontSize: 16, lineHeight: '24px', fontWeight: 700 },
    headerActions: { display: 'flex', gap: 9, alignItems: 'center' },
    primaryHeaderButton: { height: 44, display: 'inline-flex', alignItems: 'center', gap: 8, border: 0, borderRadius: 9, background: '#3D73DD', color: '#fff', padding: '0 17px', fontSize: 14, fontWeight: 850, cursor: 'pointer', boxShadow: '0 8px 18px rgba(61,115,221,0.18)' },
    refreshButton: { height: 44, display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid #D8DEE9', borderRadius: 9, background: '#fff', color: '#1A305D', padding: '0 15px', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
    refreshMeta: { color: '#71809A', fontSize: 14, lineHeight: '20px', fontWeight: 800, whiteSpace: 'nowrap' },
    explainPanel: { display: 'grid', gridTemplateColumns: '36px 1fr', gap: 13, alignItems: 'center', background: 'linear-gradient(90deg, #EFF6FF 0%, #F8FBFF 100%)', border: '1px solid #BFDBFE', borderRadius: 12, padding: '12px 16px', marginBottom: 14 },
    explainIcon: { width: 36, height: 36, borderRadius: 9, background: '#DBEAFE', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    explainContent: { minWidth: 0 },
    explainTitle: { color: '#1A305D', fontSize: 15, lineHeight: '21px', fontWeight: 900, marginBottom: 2 },
    explainText: { color: '#53627A', fontSize: 14, lineHeight: '20px', fontWeight: 700 },
    evidenceNotice: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        border: '1px solid #DDD6FE',
        borderRadius: 12,
        background: '#FAF8FF',
        color: '#6D28D9',
        padding: '13px 16px',
        marginBottom: 16,
    },
    evidenceNoticeTitle: { color: '#4C1D95', fontSize: 14, lineHeight: '20px', fontWeight: 900 },
    evidenceNoticeText: { color: '#7C6F9B', fontSize: 13, lineHeight: '19px', fontWeight: 700, marginTop: 3 },
    evidenceClearButton: { minHeight: 34, flexShrink: 0, border: '1px solid #C4B5FD', borderRadius: 8, background: '#FFFFFF', color: '#6D28D9', padding: '0 11px', fontSize: 12, lineHeight: '17px', fontWeight: 850, cursor: 'pointer', whiteSpace: 'nowrap' },
    modalOverlay: { position: 'fixed', inset: 0, zIndex: 2500, background: 'rgba(17,24,39,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
    createModal: { width: 760, maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, boxShadow: '0 24px 70px rgba(17,24,39,0.24)' },
    journalModal: { width: 820, maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, boxShadow: '0 24px 70px rgba(17,24,39,0.24)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 6 },
    modalNotice: { border: '1px solid #DCE6F8', borderRadius: 8, background: '#F8FAFC', color: '#5E7093', fontSize: 14, lineHeight: '22px', fontWeight: 750, padding: '12px 13px', marginBottom: 14 },
    iconButton: { width: 36, height: 36, border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', color: '#1A305D', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 16 },
    cancelButton: { height: 44, minWidth: 88, border: '1px solid #D8DEE9', borderRadius: 8, background: '#fff', color: '#1A305D', fontWeight: 800, cursor: 'pointer' },
    filterBar: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '14px 16px 16px', marginBottom: 14, boxShadow: '0 1px 3px rgba(26,48,93,0.04)' },
    filterHeading: { flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, marginBottom: 1 },
    filterTitle: { display: 'inline-flex', alignItems: 'center', gap: 8, color: '#1A305D', fontSize: 15, fontWeight: 850, whiteSpace: 'nowrap' },
    filterScopeNote: { color: '#71809A', fontSize: 12, lineHeight: '18px', fontWeight: 700 },
    compactFilterField: { display: 'inline-flex', flexDirection: 'column', gap: 4 },
    filterInputLabel: { color: '#64748B', fontSize: 11, lineHeight: '16px', fontWeight: 800 },
    quickRanges: { height: 42, display: 'inline-flex', alignItems: 'center', gap: 4, padding: 3, border: '1px solid #E2E8F0', borderRadius: 9, background: '#F8FAFC' },
    quickButton: { height: 34, padding: '0 12px', border: '1px solid transparent', borderRadius: 7, background: 'transparent', color: '#5E7093', fontSize: 14, fontWeight: 750, cursor: 'pointer', whiteSpace: 'nowrap' },
    quickButtonActive: { borderColor: '#3D73DD', background: '#ECF1FC', color: '#1D4ED8', boxShadow: 'inset 0 0 0 1px rgba(61,115,221,0.16)' },
    searchBox: { height: 42, minWidth: 300, display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid #D8DEE9', borderRadius: 8, padding: '0 12px', background: '#fff', color: '#71809A' },
    searchInput: { width: '100%', border: 0, outline: 'none', color: '#1A305D', fontSize: 14, fontWeight: 700, background: 'transparent' },
    searchClearButton: { width: 24, height: 24, border: 0, borderRadius: 999, background: '#F1F5F9', color: '#71809A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
    input: { height: 42, minWidth: 156, border: '1px solid #D8DEE9', borderRadius: 8, padding: '0 12px', background: '#fff', color: '#1A305D', outline: 'none', fontSize: 14, fontWeight: 700 },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, marginBottom: 18 },
    kpi: { minHeight: 128, display: 'flex', gap: 13, alignItems: 'flex-start', background: '#fff', border: '1px solid #DDE5F0', borderRadius: 12, padding: '18px 17px', boxSizing: 'border-box', boxShadow: '0 3px 12px rgba(26,48,93,0.055)' },
    kpiButton: { width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
    kpiIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    kpiLabel: { fontSize: 14, lineHeight: '20px', color: '#5E7093', marginBottom: 5, fontWeight: 850, whiteSpace: 'nowrap' },
    kpiValue: { fontSize: 29, lineHeight: '35px', fontWeight: 900, color: '#1A305D', letterSpacing: -0.3 },
    kpiSub: { marginTop: 4, fontSize: 13, lineHeight: '19px', color: '#71809A', fontWeight: 700 },
    closingSuccessPanel: { marginBottom: 18 },
    closingSuccessHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 },
    closingSuccessCounter: { display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 12px', borderRadius: 999, background: '#ECFDF5', color: '#047857', fontSize: 15, lineHeight: '21px', fontWeight: 900, whiteSpace: 'nowrap' },
    closingSuccessToolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 },
    closingSearchBox: { minWidth: 360 },
    closingTable: { minWidth: 1120 },
    coachClosingPanel: { background: '#fff', border: '1px solid #D8E2F0', borderRadius: 12, padding: 24, boxShadow: '0 8px 24px rgba(26,48,93,0.07)', marginBottom: 18 },
    coachClosingBoard: { display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #EDF1F6', borderRadius: 12, overflow: 'hidden' },
    coachClosingHeaderRow: { display: 'grid', gridTemplateColumns: 'minmax(240px, 0.85fr) minmax(360px, 1.35fr) minmax(210px, 0.65fr)', gap: 20, padding: '14px 22px', background: '#F8FAFC', color: '#5E7093', fontSize: 14, lineHeight: '20px', fontWeight: 900 },
    coachClosingRow: { display: 'grid', gridTemplateColumns: 'minmax(240px, 0.85fr) minmax(360px, 1.35fr) minmax(210px, 0.65fr)', gap: 20, alignItems: 'center', padding: '20px 22px', borderTop: '1px solid #EDF1F6', background: '#fff' },
    coachClosingIdentity: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 },
    coachRankBadge: { width: 38, height: 38, borderRadius: 10, background: '#ECF1FC', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, flexShrink: 0 },
    coachClosingName: { minWidth: 0, color: '#1A305D', fontSize: 19, lineHeight: '25px', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    coachClosingSub: { color: '#71809A', fontSize: 14, lineHeight: '20px', fontWeight: 800, marginTop: 4 },
    coachJournalSub: { color: '#6D28D9', fontSize: 13, lineHeight: '19px', fontWeight: 850, marginTop: 5 },
    coachClosingOpportunity: { minWidth: 0 },
    coachClosingRateWrap: { justifySelf: 'stretch', textAlign: 'left' },
    coachClosingResultCard: { width: '100%', border: '1px solid #E2E8F0', borderRadius: 10, background: '#F8FAFC', padding: '13px 15px', boxSizing: 'border-box' },
    coachClosingResult: { color: '#1A305D', fontSize: 18, lineHeight: '25px', fontWeight: 900, letterSpacing: 0 },
    convertedMemberList: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
    convertedMemberPill: { display: 'inline-flex', alignItems: 'center', maxWidth: 90, padding: '4px 8px', borderRadius: 999, background: '#EAF7EF', color: '#047857', fontSize: 13, lineHeight: '18px', fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    convertedMemberMore: { display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: 999, background: '#E2E8F0', color: '#5E7093', fontSize: 13, lineHeight: '18px', fontWeight: 850 },
    coachClosingBarHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 9, color: '#5E7093', fontSize: 14, lineHeight: '20px', fontWeight: 850 },
    coachClosingBarRate: { color: '#1D4ED8', fontSize: 18, lineHeight: '24px', fontWeight: 900, minWidth: 52, textAlign: 'right' },
    coachClosingMeta: { marginTop: 8, color: '#5E7093', fontSize: 14, lineHeight: '20px', fontWeight: 800 },
    coachProgressTrack: { height: 12, borderRadius: 999, background: '#E8EEF7', overflow: 'hidden' },
    coachProgressFill: { display: 'block', height: '100%', borderRadius: 999, background: '#3D73DD' },
    analyticsGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 },
    workGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 },
    panel: { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(26,48,93,0.04)' },
    panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 },
    panelTitle: { margin: 0, fontSize: 20, lineHeight: '28px', color: '#1A305D', fontWeight: 900 },
    panelCaption: { margin: '5px 0 0', color: '#71809A', fontSize: 14, lineHeight: '20px', fontWeight: 700 },
    rankingList: { display: 'flex', flexDirection: 'column', gap: 10 },
    rankingRow: { display: 'grid', gridTemplateColumns: '34px 1fr auto', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #F0F3F7' },
    rankBadge: { width: 30, height: 30, borderRadius: 8, background: '#ECF1FC', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900 },
    rankMain: { minWidth: 0 },
    rankName: { fontSize: 16, lineHeight: '22px', fontWeight: 850, color: '#1A305D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    rankMeta: { fontSize: 14, lineHeight: '20px', color: '#71809A', marginTop: 3, fontWeight: 700 },
    rankJournalMeta: { fontSize: 13, lineHeight: '19px', color: '#6D28D9', marginTop: 3, fontWeight: 800 },
    rankRate: { display: 'inline-flex', alignItems: 'center', gap: 2, color: '#1D4ED8', fontSize: 17, lineHeight: '23px', fontWeight: 900 },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 12, rowGap: 10 },
    field: { display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 12 },
    fieldLabel: { fontSize: 14, lineHeight: '20px', fontWeight: 850, color: '#5E7093' },
    inputFull: { width: '100%', height: 44, border: '1px solid #D8DEE9', borderRadius: 8, padding: '0 12px', boxSizing: 'border-box', color: '#1A305D', outline: 'none', background: '#fff', fontSize: 15, fontWeight: 700 },
    memberMatchNotice: { gridColumn: '1 / -1', borderRadius: 8, padding: '11px 13px', fontSize: 14, fontWeight: 750, lineHeight: '21px', marginTop: -2 },
    memberMatchNoticeFound: { border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8' },
    memberMatchNoticeNew: { border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#6B7280' },
    memberMatchNoticeReadOnly: { border: '1px solid #FCD34D', background: '#FFFBEB', color: '#92400E' },
    textarea: { width: '100%', minHeight: 100, border: '1px solid #D8DEE9', borderRadius: 8, padding: 12, boxSizing: 'border-box', resize: 'vertical', color: '#1A305D', outline: 'none', fontSize: 15, lineHeight: '22px', fontWeight: 700 },
    notificationToggle: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 13px', marginBottom: 12, border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC', color: '#1A305D', fontSize: 14, lineHeight: '21px', fontWeight: 850, cursor: 'pointer' },
    notificationToggleCaption: { display: 'block', marginTop: 4, color: '#71809A', fontSize: 14, lineHeight: '20px', fontWeight: 700 },
    formGuide: { minHeight: 40, display: 'flex', alignItems: 'center', border: '1px solid #DBEAFE', borderRadius: 8, background: '#EFF6FF', color: '#1D4ED8', padding: '8px 12px', fontSize: 13, lineHeight: '19px', fontWeight: 800 },
    primaryButton: { width: '100%', height: 46, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 8, border: 0, borderRadius: 8, background: '#3D73DD', color: '#fff', fontSize: 15, fontWeight: 850, cursor: 'pointer' },
    secondaryButton: { height: 42, border: 0, borderRadius: 8, background: '#1A305D', color: '#fff', padding: '0 16px', cursor: 'pointer', fontSize: 14, fontWeight: 850 },
    inline: { display: 'flex', gap: 8, marginBottom: 14 },
    inputGrow: { flex: 1, height: 42, border: '1px solid #D8DEE9', borderRadius: 8, padding: '0 12px', color: '#1A305D', outline: 'none', fontSize: 14, fontWeight: 700 },
    categoryList: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    categoryItem: { display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 999, padding: '4px', fontWeight: 850, fontSize: 14 },
    categoryToggleButton: { display: 'inline-flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent', color: 'inherit', padding: '7px 9px', cursor: 'pointer', fontWeight: 850, fontSize: 14 },
    categoryDeleteButton: { width: 30, height: 30, border: 0, borderRadius: 999, background: 'rgba(255,255,255,0.65)', color: '#DC2626', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    categoryChipActive: { border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8' },
    categoryChipInactive: { border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#9CA3AF' },
    categoryChipState: { fontSize: 12, fontWeight: 900, opacity: 0.75 },
    tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
    tableHeaderActions: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flex: '1 1 420px' },
    activeListFilter: { minHeight: 38, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #C4B5FD', borderRadius: 999, background: '#F5F3FF', color: '#6D28D9', padding: '0 11px', fontSize: 12, lineHeight: '17px', fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' },
    listSearchBox: { minWidth: 420, maxWidth: 560, flex: '1 1 420px' },
    listSearchButton: { height: 42, border: 0, borderRadius: 8, background: '#1A305D', color: '#fff', padding: '0 16px', fontSize: 14, fontWeight: 850, cursor: 'pointer', whiteSpace: 'nowrap' },
    tableWrap: { width: '100%', overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, minWidth: 1380 },
    th: { textAlign: 'left', padding: '14px 12px', color: '#5E7093', borderBottom: '1px solid #E2E8F0', fontSize: 14, lineHeight: '20px', fontWeight: 900, background: '#F8FAFC' },
    td: { padding: '16px 12px', borderBottom: '1px solid #F0F3F7', color: '#111827', verticalAlign: 'top' },
    dateCell: { display: 'inline-flex', alignItems: 'center', gap: 7, color: '#1A305D', fontWeight: 900, fontSize: 15, lineHeight: '21px' },
    nameCell: { display: 'inline-flex', alignItems: 'center', gap: 7, color: '#1A305D', fontWeight: 850, fontSize: 15, lineHeight: '21px' },
    memberName: { color: '#111827', fontWeight: 900, fontSize: 15, lineHeight: '21px' },
    subText: { marginTop: 5, color: '#71809A', fontSize: 14, lineHeight: '20px', fontWeight: 700 },
    typeBadge: { display: 'inline-block', maxWidth: 190, padding: '6px 10px', borderRadius: 999, background: '#F5F3FF', color: '#6D28D9', fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14, lineHeight: '20px', verticalAlign: 'top' },
    appLinkedBadge: { display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 10px', borderRadius: 999, background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#047857', fontWeight: 900, whiteSpace: 'nowrap', fontSize: 13 },
    appNotLinkedBadge: { display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 10px', borderRadius: 999, background: '#FFF7ED', border: '1px solid #FED7AA', color: '#C2410C', fontWeight: 900, whiteSpace: 'nowrap', fontSize: 13 },
    unknownBadge: { display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 10px', borderRadius: 999, background: '#F8FAFC', border: '1px solid #CBD5E1', color: '#64748B', fontWeight: 850, whiteSpace: 'nowrap', fontSize: 13 },
    journalViewButton: { height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #C4B5FD', borderRadius: 8, background: '#F5F3FF', color: '#6D28D9', padding: '0 10px', fontSize: 13, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' },
    journalEmptyButton: { height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC', color: '#64748B', padding: '0 10px', fontSize: 13, fontWeight: 850, whiteSpace: 'nowrap' },
    journalUnknownBadge: { height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#64748B', padding: '0 10px', fontSize: 13, fontWeight: 850, whiteSpace: 'nowrap' },
    conversionBadge: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 999, background: '#ECFDF5', color: '#047857', fontWeight: 900, whiteSpace: 'nowrap', fontSize: 14, lineHeight: '20px' },
    waitingBadge: { display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: '#F8FAFC', color: '#71809A', border: '1px solid #E2E8F0', fontWeight: 850, whiteSpace: 'nowrap', fontSize: 14, lineHeight: '20px' },
    memoEditCell: { display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto', gap: 8, alignItems: 'flex-start', minWidth: 320 },
    memoTextarea: { width: '100%', minHeight: 64, border: '1px solid #D8DEE9', borderRadius: 8, padding: '10px 11px', resize: 'vertical', color: '#1A305D', outline: 'none', boxSizing: 'border-box', fontSize: 14, lineHeight: '21px', fontWeight: 700, background: '#fff' },
    readOnlyTextarea: { background: '#F8FAFC', color: '#64748B', cursor: 'default', resize: 'none' },
    readOnlyBadge: { display: 'inline-flex', marginTop: 6, borderRadius: 999, border: '1px solid #FCD34D', background: '#FFFBEB', color: '#92400E', padding: '2px 7px', fontSize: 11, fontWeight: 800 },
    memoSaveButton: { height: 38, border: 0, borderRadius: 8, background: '#1A305D', color: '#fff', padding: '0 13px', fontSize: 14, fontWeight: 850, cursor: 'pointer', whiteSpace: 'nowrap' },
    paginationBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 4px 0', borderTop: '1px solid #F0F3F7', marginTop: 8 },
    paginationMeta: { color: '#5E7093', fontSize: 14, lineHeight: '20px', fontWeight: 800 },
    paginationControls: { display: 'inline-flex', alignItems: 'center', gap: 8 },
    paginationButton: { height: 38, border: '1px solid #D8DEE9', borderRadius: 8, background: '#fff', color: '#1A305D', padding: '0 14px', fontSize: 14, fontWeight: 850, cursor: 'pointer' },
    paginationPage: { color: '#1A305D', fontSize: 14, lineHeight: '20px', fontWeight: 900, minWidth: 56, textAlign: 'center' },
    emptyState: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#71809A', fontSize: 15, lineHeight: '22px', fontWeight: 800 },
    queryError: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        border: '1px solid #FECACA',
        borderRadius: 10,
        background: '#FEF2F2',
        color: '#B91C1C',
        padding: '14px 16px',
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 800,
    },
    queryRetryButton: {
        height: 34,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
        border: '1px solid #FCA5A5',
        borderRadius: 8,
        background: '#FFFFFF',
        color: '#B91C1C',
        padding: '0 11px',
        fontSize: 13,
        fontWeight: 850,
        cursor: 'pointer',
    },
    srOnly: {
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
};

export default NewOpportunityPage;
